// src/GDB/pages/DatasetBuilderPage.jsx
import { useEffect, useMemo, useState } from "react";
import { apiGet, apiPost, apiPut, apiDelete } from "../api/client";
import JiraDatasetForm from "../components/JiraDatasetForm";
import "../styles/DatasetBuilderPage.css";

export default function DatasetBuilderPage() {
  const [dataSources, setDataSources] = useState([]);
  const [selectedDsId, setSelectedDsId] = useState("");
  const [tables, setTables] = useState([]);
  const [baseTable, setBaseTable] = useState("");
  const [columnsBase, setColumnsBase] = useState([]);
  const [columnsByTable, setColumnsByTable] = useState({});
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [joins, setJoins] = useState([]);

  const [datasetName, setDatasetName] = useState("");
  const [datasetDescription, setDatasetDescription] = useState("");
  const [previewSql, setPreviewSql] = useState("");

  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Existing datasets + selection + preview rows (unchanged)
  const [datasets, setDatasets] = useState([]);
  const [selectedDatasetId, setSelectedDatasetId] = useState("");
  const [datasetPreviewRows, setDatasetPreviewRows] = useState([]);

  // NEW: ad-hoc test query result (Run Query (test))
  const [testRows, setTestRows] = useState([]);
  const [testColumns, setTestColumns] = useState([]);

  // ---- Check if selected datasource is JIRA ----
  const selectedDatasource = useMemo(() => {
    return dataSources.find((ds) => ds.id === Number(selectedDsId));
  }, [dataSources, selectedDsId]);

  const isJiraDatasource = useMemo(() => {
    return selectedDatasource?.db_type?.toLowerCase() === "jira";
  }, [selectedDatasource]);

  // ---- Helpers ----

  const loadDatasources = async () => {
    setIsLoading(true);
    try {
      const res = await apiGet("/datasources/");
      setDataSources(res);
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  const loadDatasets = async () => {
    try {
      const res = await apiGet("/datasets/");
      setDatasets(res);
    } catch (e) {
      console.error(e);
    }
  };

  // Load datasources + datasets on mount
  useEffect(() => {
    loadDatasources();
    loadDatasets();
  }, []);

  const loadTables = async (dsId) => {
    if (!dsId) return;
    setIsLoading(true);
    try {
      const res = await apiGet(`/datasources/${dsId}/tables`);
      setTables(res.tables || []);
    } finally {
      setIsLoading(false);
    }
  };

  const loadColumns = async (table) => {
    if (!table || !selectedDsId) return [];
    setIsLoading(true);
    try {
      const res = await apiGet(
        `/datasources/${selectedDsId}/columns?table=${encodeURIComponent(
          table
        )}`
      );
      return res.columns || [];
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectDataSource = async (e) => {
    const id = e.target.value;
    setSelectedDsId(id);
    setBaseTable("");
    setTables([]);
    setColumnsBase([]);
    setColumnsByTable({});
    setSelectedColumns([]);
    setJoins([]);
    setPreviewSql("");
    setTestRows([]);
    setTestColumns([]);
    setSelectedDatasetId("");
    setDatasetName("");
    setDatasetDescription("");
    setDatasetPreviewRows([]);
    setMessage("");

    // Only load tables for non-JIRA datasources
    const ds = dataSources.find((d) => d.id === Number(id));
    if (id && ds?.db_type?.toLowerCase() !== "jira") {
      await loadTables(id);
    }
  };

  const handleSelectBaseTable = async (e) => {
    const tbl = e.target.value;
    setBaseTable(tbl);
    setSelectedColumns([]);
    setJoins([]);
    setPreviewSql("");
    setTestRows([]);
    setTestColumns([]);

    if (tbl) {
      const cols = await loadColumns(tbl);
      setColumnsBase(cols);
      setColumnsByTable((prev) => ({ ...prev, [tbl]: cols }));
    } else {
      setColumnsBase([]);
    }
  };

  const toggleColumnSelection = (table, column) => {
    const key = `${table}.${column}`;
    setSelectedColumns((prev) => {
      const exists = prev.find((c) => `${c.table}.${c.column}` === key);
      if (exists) {
        return prev.filter((c) => `${c.table}.${c.column}` !== key);
      }
      return [...prev, { table, column }];
    });
  };

  const handleAddJoinRow = async () => {
    if (!baseTable) {
      setMessage("Select base table first.");
      return;
    }
    setJoins((prev) => [
      ...prev,
      {
        type: "INNER",
        joinTable: "",
        baseColumn: "",
        joinColumn: "",
        joinColumns: [],
      },
    ]);
  };

  const handleJoinChange = async (idx, patch) => {
    setJoins((prev) =>
      prev.map((j, i) => (i === idx ? { ...j, ...patch } : j))
    );
  };

  const handleJoinTableSelect = async (idx, tableName) => {
    if (tableName && !columnsByTable[tableName]) {
      const cols = await loadColumns(tableName);
      setColumnsByTable((prev) => ({ ...prev, [tableName]: cols }));
    }
    const cols = columnsByTable[tableName] || [];
    setJoins((prev) =>
      prev.map((j, i) =>
        i === idx ? { ...j, joinTable: tableName, joinColumns: cols } : j
      )
    );
  };

  const removeJoin = (idx) => {
    setJoins((prev) => prev.filter((_, i) => i !== idx));
  };

  const buildSql = () => {
    if (!baseTable) return "";

    const cols =
      selectedColumns.length > 0
        ? selectedColumns.map((c) => `${c.table}.${c.column}`)
        : ["*"];

    let sql = `SELECT ${cols.join(", ")}\nFROM ${baseTable}`;

    joins.forEach((j) => {
      if (!j.joinTable || !j.baseColumn || !j.joinColumn) return;
      sql += `\n  ${j.type} JOIN ${j.joinTable} ON ${baseTable}.${j.baseColumn} = ${j.joinTable}.${j.joinColumn}`;
    });

    return sql;
  };

  const handlePreviewSqlFromBuilder = () => {
    const sql = buildSql();
    setPreviewSql(sql);
    // keep test rows as is; they belong to last run
  };

  const validateDatasetCommon = () => {
    if (!datasetName) {
      setMessage("Dataset name is required.");
      return false;
    }
    if (!selectedDsId) {
      setMessage("Select a data source.");
      return false;
    }
    const sql = previewSql || buildSql();
    if (!sql) {
      setMessage("Nothing to save: build or enter a query first.");
      return false;
    }
    return true;
  };

  const handleSaveDataset = async () => {
    setMessage("");
    if (!validateDatasetCommon()) return;

    const sql = previewSql || buildSql();

    setIsLoading(true);
    try {
      const body = {
        name: datasetName,
        description: datasetDescription,
        datasource_id: Number(selectedDsId),
        sql_query: sql,
      };
      const res = await apiPost("/datasets/", body);
      setMessage(`Dataset saved successfully! (ID: ${res.id})`);
      setDatasetName("");
      setDatasetDescription("");
      setPreviewSql("");
      setSelectedDatasetId("");
      setDatasetPreviewRows([]);
      setTestRows([]);
      setTestColumns([]);
      await loadDatasets();
    } catch (e) {
      console.error(e);
      setMessage(`Error saving dataset: ${e}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateDataset = async () => {
    setMessage("");
    if (!selectedDatasetId) {
      setMessage("Select a dataset to update.");
      return;
    }
    if (!validateDatasetCommon()) return;

    const sql = previewSql || buildSql();

    setIsLoading(true);
    try {
      const body = {
        name: datasetName,
        description: datasetDescription,
        datasource_id: Number(selectedDsId),
        sql_query: sql,
      };
      const res = await apiPut(`/datasets/${selectedDatasetId}`, body);
      setMessage(`Dataset updated successfully! (ID: ${res.id})`);
      await loadDatasets();
      // reload preview after update
      await loadDatasetPreview(selectedDatasetId);
      setTestRows([]);
      setTestColumns([]);
    } catch (e) {
      console.error(e);
      setMessage(`Error updating dataset: ${e}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteDataset = async () => {
    if (!selectedDatasetId) {
      setMessage("Select a dataset to delete.");
      return;
    }
    const ok = window.confirm("Delete this dataset?");
    if (!ok) return;

    setIsLoading(true);
    try {
      await apiDelete(`/datasets/${selectedDatasetId}`);
      setMessage("Dataset deleted.");
      setSelectedDatasetId("");
      setDatasetName("");
      setDatasetDescription("");
      setPreviewSql("");
      setDatasetPreviewRows([]);
      setTestRows([]);
      setTestColumns([]);
      await loadDatasets();
    } catch (e) {
      console.error(e);
      setMessage(`Error deleting dataset: ${e}`);
    } finally {
      setIsLoading(false);
    }
  };

  const loadDatasetPreview = async (id) => {
    setDatasetPreviewRows([]);
    if (!id) return;
    try {
      const res = await apiGet(`/query/dataset/${id}`);
      setDatasetPreviewRows(res.data || []);
    } catch (e) {
      console.error(e);
      setMessage("Error loading dataset preview.");
    }
  };

  const handleSelectDataset = async (e) => {
    const id = e.target.value;
    setSelectedDatasetId(id);
    setMessage("");
    setDatasetPreviewRows([]);
    setTestRows([]);
    setTestColumns([]);

    if (!id) {
      // reset form to "new dataset" mode
      setDatasetName("");
      setDatasetDescription("");
      setPreviewSql("");
      return;
    }

    const numericId = Number(id);
    const ds = datasets.find((d) => d.id === numericId);
    if (ds) {
      setDatasetName(ds.name || "");
      setDatasetDescription(ds.description || "");
      setPreviewSql(ds.sql_query || "");
      if (ds.datasource_id) {
        setSelectedDsId(String(ds.datasource_id));
        // Check if it's a JIRA datasource
        const datasource = dataSources.find((d) => d.id === ds.datasource_id);
        if (datasource?.db_type?.toLowerCase() !== "jira") {
          await loadTables(ds.datasource_id);
        }
      }
    }

    await loadDatasetPreview(numericId);
  };

  // ---- AI: Generate SQL using Data Source + Description ----
  const handleGenerateSqlWithAI = async () => {
    setMessage("");

    if (!selectedDsId) {
      setMessage("Select a data source before using AI.");
      return;
    }
    if (!datasetDescription.trim()) {
      setMessage("Enter a dataset description for AI.");
      return;
    }

    setIsLoading(true);
    try {
      const body = {
        datasource_id: Number(selectedDsId),
        goal: datasetDescription,
      };
      const res = await apiPost("/ai/generate-dataset-sql", body);

      if (res.sql_query) {
        setPreviewSql(res.sql_query);
      }
      if (!datasetName && res.suggested_name) {
        setDatasetName(res.suggested_name);
      }
      if (!datasetDescription && res.description) {
        setDatasetDescription(res.description);
      }
      setMessage("✨ SQL generated by AI. Review and adjust if needed.");
    } catch (e) {
      console.error(e);
      setMessage("Error generating SQL with AI.");
    } finally {
      setIsLoading(false);
    }
  };

  // ---- Run Query (test) without saving ----
  const handleRunQueryTest = async () => {
    setMessage("");
    setTestRows([]);
    setTestColumns([]);

    if (!selectedDsId) {
      setMessage("Select a data source before running the query.");
      return;
    }

    const sql = (previewSql || buildSql()).trim();
    if (!sql) {
      setMessage("Nothing to run: build or enter a query first.");
      return;
    }

    setIsLoading(true);
    try {
      const body = {
        datasource_id: Number(selectedDsId),
        sql,
      };
      const res = await apiPost("/query/test", body); // backend endpoint you added
      const rows = res.data || [];
      setTestRows(rows);
      if (rows.length > 0) {
        setTestColumns(Object.keys(rows[0]));
      } else {
        setTestColumns([]);
      }
      setMessage(`Query executed. Returned ${rows.length} row(s).`);
    } catch (e) {
      console.error(e);
      setMessage("Error running query: " + e);
    } finally {
      setIsLoading(false);
    }
  };

  // ---- JIRA Dataset handlers ----
  const handleJiraDatasetSaved = () => {
    setMessage("✅ JIRA dataset saved successfully!");
    loadDatasets();
    setSelectedDatasetId("");
  };

  const handleJiraDatasetCancel = () => {
    setSelectedDatasetId("");
    setMessage("");
  };

  // Get the selected dataset data for JIRA form
  const selectedDatasetData = useMemo(() => {
    if (!selectedDatasetId) return null;
    return datasets.find((d) => d.id === Number(selectedDatasetId)) || null;
  }, [selectedDatasetId, datasets]);

  const baseTableColumnsNames = useMemo(
    () => (columnsBase || []).map((c) => c.name),
    [columnsBase]
  );

  // derive preview table headers for SAVED dataset preview
  const datasetPreviewHeaders =
    datasetPreviewRows && datasetPreviewRows.length
      ? Object.keys(datasetPreviewRows[0])
      : [];

  // Filter datasets by selected datasource
  const filteredDatasets = useMemo(() => {
    if (!selectedDsId) return datasets;
    return datasets.filter((d) => d.datasource_id === Number(selectedDsId));
  }, [datasets, selectedDsId]);

  return (
    <div className="dataset-builder">
      <div className="builder-header">
        <h1 className="builder-title">Dataset Builder</h1>
        <p className="builder-subtitle">Create and manage your datasets</p>
      </div>

      <div className="builder-layout">
        {/* Left Panel - Builder (hidden for JIRA) */}
        {!isJiraDatasource && (
          <div className="builder-panel">
            {/* Data Source Selection */}
            <div className="builder-card">
              <h3 className="card-title">Data Source</h3>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Data Source</label>
                  <select
                    value={selectedDsId}
                    onChange={handleSelectDataSource}
                    className="form-select"
                    disabled={isLoading}
                  >
                    <option value="">Select data source</option>
                    {dataSources.map((ds) => (
                      <option key={ds.id} value={ds.id}>
                        {ds.name} ({ds.db_type})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Base Table</label>
                  <select
                    value={baseTable}
                    onChange={handleSelectBaseTable}
                    disabled={!selectedDsId || isLoading}
                    className="form-select"
                  >
                    <option value="">Select base table</option>
                    {tables.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Columns Selection */}
            <div className="builder-card">
              <div className="card-header">
                <h3 className="card-title">Columns</h3>
                <span className="selection-count">
                  {selectedColumns.length} selected
                </span>
              </div>

              {baseTable ? (
                <div className="columns-grid">
                  {(columnsBase || []).map((c) => {
                    const key = `${baseTable}.${c.name}`;
                    const checked = !!selectedColumns.find(
                      (s) => `${s.table}.${s.column}` === key
                    );
                    return (
                      <label
                        key={key}
                        className={`column-item ${
                          checked ? "column-item--selected" : ""
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() =>
                            toggleColumnSelection(baseTable, c.name)
                          }
                          className="column-checkbox"
                        />
                        <div className="column-info">
                          <div className="column-name">{c.name}</div>
                          <div className="column-type">{c.type}</div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              ) : (
                <div className="empty-state">
                  <div className="empty-icon">📊</div>
                  <p>Select a base table to load columns</p>
                </div>
              )}
            </div>

            {/* Joins */}
            <div className="builder-card">
              <div className="card-header">
                <h3 className="card-title">Joins</h3>
                <button
                  type="button"
                  onClick={handleAddJoinRow}
                  disabled={!baseTable || isLoading}
                  className="btn btn--primary btn--sm"
                >
                  + Add Join
                </button>
              </div>

              {joins.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">🔗</div>
                  <p>No joins configured</p>
                  <p className="empty-hint">
                    Add joins to combine multiple tables
                  </p>
                </div>
              ) : (
                <div className="joins-list">
                  {joins.map((j, idx) => {
                    const joinCols = j.joinColumns || [];
                    return (
                      <div key={`join-${idx}`} className="join-item">
                        <div className="join-header">
                          <h4>Join {idx + 1}</h4>
                          <button
                            onClick={() => removeJoin(idx)}
                            className="btn btn--icon"
                            aria-label="Remove join"
                          >
                            ×
                          </button>
                        </div>

                        <div className="join-form">
                          <div className="form-row">
                            <div className="form-group">
                              <label className="form-label">Join Type</label>
                              <select
                                value={j.type}
                                onChange={(e) =>
                                  handleJoinChange(idx, {
                                    type: e.target.value,
                                  })
                                }
                                className="form-select"
                              >
                                <option value="INNER">INNER JOIN</option>
                                <option value="LEFT">LEFT JOIN</option>
                                <option value="RIGHT">RIGHT JOIN</option>
                              </select>
                            </div>

                            <div className="form-group">
                              <label className="form-label">Join Table</label>
                              <select
                                value={j.joinTable}
                                onChange={(e) =>
                                  handleJoinTableSelect(idx, e.target.value)
                                }
                                className="form-select"
                              >
                                <option value="">Select table</option>
                                {tables
                                  .filter((t) => t !== baseTable)
                                  .map((t) => (
                                    <option key={t} value={t}>
                                      {t}
                                    </option>
                                  ))}
                              </select>
                            </div>
                          </div>

                          <div className="form-row">
                            <div className="form-group">
                              <label className="form-label">Base Column</label>
                              <select
                                value={j.baseColumn}
                                onChange={(e) =>
                                  handleJoinChange(idx, {
                                    baseColumn: e.target.value,
                                  })
                                }
                                className="form-select"
                              >
                                <option value="">Select column</option>
                                {baseTableColumnsNames.map((cname) => (
                                  <option key={cname} value={cname}>
                                    {baseTable}.{cname}
                                  </option>
                                ))}
                              </select>
                            </div>

                            <div className="form-group">
                              <label className="form-label">Join Column</label>
                              <select
                                value={j.joinColumn}
                                onChange={(e) =>
                                  handleJoinChange(idx, {
                                    joinColumn: e.target.value,
                                  })
                                }
                                className="form-select"
                              >
                                <option value="">Select column</option>
                                {joinCols.map((c) => (
                                  <option key={c.name} value={c.name}>
                                    {j.joinTable}.{c.name}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Right Panel - Dataset Details + Existing Datasets + Preview */}
        <div className={`details-panel ${isJiraDatasource ? "details-panel--full" : ""}`}>
          {/* Data Source Selection for JIRA (shown at top when JIRA mode) */}
          {isJiraDatasource && (
            <div className="builder-card">
              <h3 className="card-title">Data Source</h3>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Data Source</label>
                  <select
                    value={selectedDsId}
                    onChange={handleSelectDataSource}
                    className="form-select"
                    disabled={isLoading}
                  >
                    <option value="">Select data source</option>
                    {dataSources.map((ds) => (
                      <option key={ds.id} value={ds.id}>
                        {ds.name} ({ds.db_type})
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="datasource-badge">
                <span className="badge badge--jira">🔷 JIRA Datasource</span>
                <p className="badge-hint">
                  JIRA uses JQL for filtering. Aggregations (COUNT, GROUP BY) are applied after fetching issues.
                </p>
              </div>
            </div>
          )}

          {/* Existing Datasets */}
          <div className="builder-card">
            <h3 className="card-title">Existing Datasets</h3>
            <div className="form-group">
              <label className="form-label">Select Dataset</label>
              <select
                value={selectedDatasetId}
                onChange={handleSelectDataset}
                className="form-select"
              >
                <option value="">+ New Dataset</option>
                {filteredDatasets.map((ds) => (
                  <option key={ds.id} value={ds.id}>
                    {ds.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Data preview of SAVED dataset (for non-JIRA or when viewing existing) */}
            {selectedDatasetId && !isJiraDatasource && (
              <div className="dataset-preview">
                <h4 className="preview-title">
                  Preview (first rows from dataset)
                </h4>
                {datasetPreviewRows.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-icon">👀</div>
                    <p>No data to display.</p>
                  </div>
                ) : (
                  <div className="preview-table-wrapper">
                    <table className="preview-table">
                      <thead>
                        <tr>
                          {datasetPreviewHeaders.map((h) => (
                            <th key={h}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {datasetPreviewRows.map((row, idx) => (
                          <tr key={idx}>
                            {datasetPreviewHeaders.map((h) => (
                              <td key={h}>{String(row[h] ?? "")}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* JIRA Dataset Form */}
          {isJiraDatasource ? (
            <div className="builder-card">
              <div className="card-header">
                <h3 className="card-title">
                  {selectedDatasetId ? "Edit JIRA Dataset" : "New JIRA Dataset"}
                </h3>
              </div>
              <JiraDatasetForm
                datasourceId={Number(selectedDsId)}
                onSave={handleJiraDatasetSaved}
                onCancel={handleJiraDatasetCancel}
                initialData={selectedDatasetData}
              />
            </div>
          ) : (
            <>
              {/* Dataset Details + AI + SQL Editor (for non-JIRA) */}
              <div className="builder-card">
                <h3 className="card-title">
                  {selectedDatasetId ? "Edit Dataset" : "Dataset Details"}
                </h3>

                <div className="form-group">
                  <label className="form-label">Dataset Name *</label>
                  <input
                    value={datasetName}
                    onChange={(e) => setDatasetName(e.target.value)}
                    placeholder="Enter dataset name"
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Description</label>
                  <textarea
                    value={datasetDescription}
                    onChange={(e) => setDatasetDescription(e.target.value)}
                    placeholder="Describe your dataset..."
                    rows={3}
                    className="form-textarea"
                  />
                </div>

                {/* AI helper block */}
                <div className="form-group ai-helper">
                  <label className="form-label">AI Assist</label>
                  <p className="ai-helper-text">
                    AI will use the selected Data Source and the Description above
                    to generate a SQL query automatically (searching all tables in
                    the database).
                  </p>
                  <div className="ai-actions">
                    <button
                      type="button"
                      onClick={handleGenerateSqlWithAI}
                      disabled={isLoading || !selectedDsId}
                      className="btn btn--secondary btn--sm"
                    >
                      ✨ Generate SQL with AI
                    </button>
                  </div>
                </div>

                {/* SQL Editor */}
                <div className="form-group">
                  <label className="form-label">SQL Query</label>
                  <div className="sql-preview">
                    <textarea
                      className="sql-textarea"
                      rows={8}
                      value={previewSql}
                      onChange={(e) => setPreviewSql(e.target.value)}
                      placeholder="SELECT ... FROM ..."
                    />
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="action-buttons">
                  <button
                    type="button"
                    onClick={handlePreviewSqlFromBuilder}
                    disabled={!baseTable || isLoading}
                    className="btn btn--secondary"
                  >
                    Build SQL from Builder
                  </button>

                  <button
                    type="button"
                    onClick={handleRunQueryTest}
                    disabled={isLoading || !selectedDsId}
                    className="btn btn--secondary"
                  >
                    ▶ Run Query (test)
                  </button>

                  {selectedDatasetId ? (
                    <>
                      <button
                        type="button"
                        onClick={handleUpdateDataset}
                        disabled={isLoading}
                        className="btn btn--primary"
                      >
                        {isLoading ? "Updating..." : "Update Dataset"}
                      </button>
                      <button
                        type="button"
                        onClick={handleDeleteDataset}
                        disabled={isLoading}
                        className="btn btn--danger"
                      >
                        Delete
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={handleSaveDataset}
                      disabled={isLoading}
                      className="btn btn--primary"
                    >
                      {isLoading ? "Saving..." : "Save Dataset"}
                    </button>
                  )}
                </div>

                {message && (
                  <div
                    className={`message ${
                      message.includes("Error") || message.includes("required")
                        ? "message--error"
                        : "message--success"
                    }`}
                  >
                    {message}
                  </div>
                )}
              </div>

              {/* Test Query Result (ad-hoc) */}
              <div className="builder-card">
                <div className="card-header">
                  <h3 className="card-title">Query Result (Test)</h3>
                  {testRows.length > 0 && (
                    <span className="selection-count">
                      {testRows.length} rows
                    </span>
                  )}
                </div>
                <div className="preview-table-wrapper">
                  {testRows.length > 0 ? (
                    <table className="preview-table">
                      <thead>
                        <tr>
                          {testColumns.map((col) => (
                            <th key={col}>{col}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {testRows.map((row, idx) => (
                          <tr key={idx}>
                            {testColumns.map((col) => (
                              <td key={col}>
                                {row[col] !== null && row[col] !== undefined
                                  ? String(row[col])
                                  : ""}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div className="preview-empty">
                      No test result yet. Build/edit SQL and click
                      {" "}
                      <strong>Run Query (test)</strong>.
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {/* Message for JIRA mode */}
          {isJiraDatasource && message && (
            <div
              className={`message ${
                message.includes("Error") || message.includes("❌")
                  ? "message--error"
                  : "message--success"
              }`}
            >
              {message}
            </div>
          )}
        </div>
      </div>

      {isLoading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
        </div>
      )}

      <style>{`
        .details-panel--full {
          grid-column: 1 / -1;
        }
        
        .datasource-badge {
          margin-top: 12px;
          padding: 12px;
          background: #eff6ff;
          border-radius: 8px;
          border: 1px solid #bfdbfe;
        }
        
        .badge--jira {
          display: inline-block;
          padding: 4px 12px;
          background: #0052cc;
          color: white;
          border-radius: 4px;
          font-size: 0.875rem;
          font-weight: 500;
        }
        
        .badge-hint {
          margin: 8px 0 0 0;
          font-size: 0.8rem;
          color: #1e40af;
        }
        
        .builder-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1f2937;
          margin: 0 0 4px 0;
        }
      `}</style>
    </div>
  );
}